import type { LocationQueryRaw, RouteParamsRaw } from 'vue-router';

import { appLogger } from '@nora-monorepo/ui-shared';

import { router } from '#nora-ui/router';
import type { RouteName } from '#nora-ui/router/route-names';

// The workspace root has no named route (it renders the bare map backdrop), so
// it is the one location addressed by path rather than name.
const WORKSPACE_PATH = '/';

function logNavigationFailure(error: unknown): void {
  appLogger.error({
    message: 'Navigation failed',
    sensitiveContext: {
      error: {
        message: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Unknown error',
        stack: error instanceof Error ? error.stack : 'No stack trace',
      },
    },
  });
}

/**
 * First segment of a route name (`admin/users` -> `admin`) — its top-level
 * workflow. Route names mirror the route hierarchy, so this identifies which
 * workflow a route belongs to without consulting the path.
 */
function workflowSegment(name: string): string {
  return name.split('/')[0];
}

/**
 * Navigate to a named route (with optional params and query), logging rather
 * than throwing on failure. Uses the singleton router, so it is safe to call
 * from non-component code where `useRouter()` is unavailable.
 */
export function navigateToRoute(name: RouteName, params?: RouteParamsRaw, query?: LocationQueryRaw): void {
  router.push({ name, params, query }).catch(logNavigationFailure);
}

/**
 * The current value of a route path param, as vue-router stores it (a string,
 * or string[] for repeatable params; `undefined` when absent). For carrying a
 * param over to the next navigation from non-component code.
 */
export function currentRouteParam(name: string): string | string[] | undefined {
  const value = router.currentRoute.value.params[name];

  return value === '' ? undefined : value;
}

/**
 * True when the current route is `name` with `params[param] === value` — e.g.
 * to test whether a just-deleted entity is the one open in the URL.
 */
export function isRouteParamActive(name: RouteName, param: string, value: string): boolean {
  const route = router.currentRoute.value;

  return route.name === name && route.params[param] === value;
}

/**
 * Navigate to the workspace root (the map backdrop with no panel or overlay).
 */
export function navigateToWorkspace(): void {
  router.push(WORKSPACE_PATH).catch(logNavigationFailure);
}

/**
 * Toggle a top-level workflow: navigate to `name`, or back to the workspace
 * root if the current route is already in that workflow.
 */
export function toggleRoute(name: RouteName): void {
  const currentName = router.currentRoute.value.name;
  const alreadyActive
    = typeof currentName === 'string'
      && workflowSegment(currentName) === workflowSegment(name);

  if (alreadyActive) {
    navigateToWorkspace();
  }
  else {
    navigateToRoute(name);
  }
}
